import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight, FaClock, FaUser, FaTag, FaFire,
  FaBook, FaChevronRight, FaSearch, FaComment,
  FaThumbsUp, FaShare, FaBookmark, FaAward,
  FaLightbulb, FaQuoteLeft,
  FaRegClock
} from "react-icons/fa";
import SEOHelmet from '../SEOHelmet';
import Navbar from "./Navbar";
import { buildBlogPath } from '../../utils/slugify';
import "../../styles/landing-page/BlogsPage.css";
import heroimg from '../../assets/BlogHero.png';
// Remove hardcoded data - now fetching from API

const Blog = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('blog');
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);

  // Fetch blogs from API
  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/all?page=${currentPage}&limit=15&status=published`
      );
      if (response.data.success) {
        setBlogs(response.data.data);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Strip HTML and limit text
  const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const limitText = (text, limit = 150) => {
    const cleanText = stripHtml(text);
    return cleanText.length > limit ? cleanText.substring(0, limit) + '...' : cleanText;
  };

  // Get featured post (first blog)
  const featuredPost = blogs.length > 0 ? blogs[0] : null;

  // Get other posts (exclude featured)
  const otherPosts = blogs.slice(1, 10);

  // Filter and get trending posts (sort by views)
  const trendingPosts = blogs
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 5)
    .map((blog, i) => ({
      title: blog.title,
      views: `${(Math.random() * 15 + 5).toFixed(1)}K`,
      category: blog.category,
      _id: blog._id
    }));

  // Get unique authors
  const getAuthors = () => {
    const authorMap = {};
    blogs.forEach(blog => {
      if (blog.author) {
        const authorName = blog.author.name || 'Anonymous';
        if (!authorMap[authorName]) {
          authorMap[authorName] = {
            name: authorName,
            role: blog.author.role || 'Contributor',
            posts: 0,
            avatar: authorName.split(' ').map(n => n[0]).join('')
          };
        }
        authorMap[authorName].posts++;
      }
    });
    return Object.values(authorMap).slice(0, 4);
  };

  const authors = getAuthors();

  return (
  <div className="blog-page bg-background">
    <SEOHelmet page="blogs" canonicalUrl="https://www.assetflows.in/blogs" />
    {/* Hero */}
    <Navbar activeSection="blog" />
    <section className="blog-hero px-6 py-20 md:py-28 lg:py-32 blogs-gradient-hero">
      <div className="blog-hero__wrapper">
        <div className="blog-hero__content relative z-10 mx-auto max-w-3xl">
          <div className="fade-in-up">
           
            <h1 className="font-display mt-5 text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl lg:text-6xl text-primary-foreground">
              Insights & <span className="text-shimmer">Ideas</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-primary-foreground/70 leading-relaxed max-w-2xl">
              Stories, guides, and expert perspectives on building better products and scaling faster.
            </p>
          </div>

          <div className="mt-8 mx-auto relative fade-in-up" style={{ animationDelay: '0.2s' }}>
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-foreground/40" size={18} />
            <input
              placeholder="Search articles..."
              className="pl-10 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 h-12 rounded-xl w-full border"
              style={{ transition: 'all 0.3s ease' }}
            />
          </div>

          {/* Hero Stats */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { num: "500+", label: "Articles" },
              { num: "50K+", label: "Readers" },
              { num: "20+", label: "Authors" },
            ].map((s, i) => (
              <div key={s.label} className="text-center fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="font-display text-2xl font-bold text-secondary">{s.num}</div>
                <div className="text-xs text-primary-foreground/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="blog-hero__image">
          <img src={heroimg} alt="Asset Management Dashboard" />
        </div>
      </div>
    </section>

    {/* Categories 
    <section className="px-6 py-8 border-b border-border blogs-gradient-categories">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map((cat, i) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`category-chip fade-in-up ${selectedCategory === cat ? "category-chip-active" : ""}`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
    </section>*/}

    {/* Featured Post */}
    {featuredPost && (
    <section className="featured-section px-6 py-20 md:py-28 blogs-gradient-featured">
      <Navbar activeSection={activeSection} setActiveSection={setActiveSection} />
      <div className="mx-auto max-w-6xl">
        <div className="featured-post grid md:grid-cols-2 gap-0 overflow-hidden fade-in-up">
          <div className="featured-post-image">
            <img src={featuredPost.featuredImage || 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=800&q=80'} alt={featuredPost.title} className="w-full h-full object-cover" />
            <div className="featured-overlay" />
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <span className="inline-badge-featured w-fit bg-secondary text-secondary-foreground border-0 mb-4 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <FaRegClock size={12} /> Featured
            </span>
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">{featuredPost.category}</span>
            <h2 className="font-display mt-2 text-2xl md:text-3xl font-bold text-foreground leading-tight">
              {featuredPost.title}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">{limitText(featuredPost.description || featuredPost.content, 200)}</p>
            <div className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><FaUser size={14} /> {featuredPost.author?.name || 'Anonymous'}</span>
              <span className="flex items-center gap-1"><FaClock size={14} /> {new Date(featuredPost.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button 
                onClick={() => navigate(buildBlogPath(featuredPost))}
                className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 gap-2 rounded-lg font-semibold flex items-center transition-all"
              >
                Read Article <FaArrowRight size={16} />
              </button>
              <button className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors">
                <FaBookmark size={18} />
              </button>
              <button className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors">
                <FaShare size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    )}

    {/* Blog Grid */}
    <section className="px-6 py-10 md:py-16 blogs-gradient-grid">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-10 fade-in">
          <h2 className="font-display text-2xl font-bold text-foreground md:text-3xl">Latest Articles</h2>
          <span className="text-sm text-muted-foreground">{blogs.length} articles</span>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading articles...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        ) : otherPosts.length > 0 ? (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {otherPosts.map((post, i) => (
                <article key={post._id} className="blog-card fade-in-up" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div
                    className="blog-card-image cursor-pointer"
                    onClick={() => navigate(buildBlogPath(post))}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(buildBlogPath(post));
                      }
                    }}
                  >
                    <img src={post.featuredImage || 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&q=80'} alt={post.title} className="w-full h-full object-cover" />
                    <div className="blog-card-category">
                      <FaTag size={10} /> {post.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold text-foreground leading-snug">{post.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">{limitText(post.description || post.content, 100)}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><FaUser size={12} /> {post.author?.name || 'Anonymous'}</span>
                      <span className="flex items-center gap-1"><FaClock size={12} /> {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><FaThumbsUp size={12} /> {post.likes?.length || 0}</span>
                        <span className="flex items-center gap-1"><FaComment size={12} /> {post.comments?.length || 0}</span>
                      </div>
                      <div 
                        onClick={() => navigate(buildBlogPath(post))}
                        className="flex items-center gap-2 text-xs font-medium text-primary cursor-pointer hover:gap-3 transition-all"
                      >
                        Read more <FaArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-12 text-center fade-in">
              <button 
                onClick={() => setCurrentPage(currentPage + 1)}
                className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 mx-auto px-4 py-2 fw-semibold hover:bg-primary hover:text-white"
              >
                Load More Articles <FaChevronRight size={16} />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No articles found</p>
          </div>
        )}
      </div>
    </section>

    {/* Trending + Authors Sidebar Layout */}
    <section className="px-6 py-20 md:py-28 blogs-gradient-trending">
      <div className="mx-auto max-w-6xl grid lg:grid-cols-3 gap-10">

        {/* Trending Posts */}
        {trendingPosts.length > 0 && (
        <div className="lg:col-span-2 fade-in-up">
          <div className="flex items-center gap-2 mb-8">
            <FaFire size={20} className="text-secondary" />
            <h2 className="font-display text-2xl font-bold text-foreground">Trending Now</h2>
          </div>
          <div className="space-y-5">
            {trendingPosts.map((post, i) => (
              <div
                key={post._id}
                className="trending-item flex items-center gap-5 p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer fade-in-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <span className="trending-number font-display text-3xl font-bold text-primary/20">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">{post.title}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{post.category}</span>
                    <span>•</span>
                    <span>{post.views} views</span>
                  </div>
                </div>
                <FaArrowRight size={16} className="text-muted-foreground" />
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Top Authors */}
        {authors.length > 0 && (
        <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-2 mb-6">
            <FaAward size={20} className="text-secondary" />
            <h2 className="font-display text-xl font-bold text-foreground">Top Authors</h2>
          </div>
          <div className="space-y-4">
            {authors.map((author, i) => (
              <div
                key={author.name}
                className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-all cursor-pointer fade-in-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                  {author.avatar}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground text-sm">{author.name}</h4>
                  <p className="text-xs text-muted-foreground">{author.role}</p>
                </div>
                <span className="text-xs text-muted-foreground">{author.posts} posts</span>
              </div>
            ))}
          </div>

          {/* Quick Links */}
          <div className="mt-8 p-5 rounded-xl border border-border bg-card fade-in-up" style={{ animationDelay: '0.4s' }}>
            <h3 className="font-display text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <FaLightbulb size={16} className="text-secondary" /> Quick Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {["React", "TypeScript", "DevOps", "AI/ML", "APIs", "Cloud", "Security", "Performance", "Testing", "Design"].map(tag => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-medium hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </section>

    {/* Testimonial / Reader Quote */}
    <section className="cta-gradient px-6 py-16 md:py-20 blogs-gradient-quote">
      <div className="mx-auto max-w-3xl text-center fade-in-up">
        <FaQuoteLeft size={40} className="mx-auto mb-4 text-secondary" />
        <blockquote className="font-display text-xl md:text-2xl font-medium text-foreground leading-relaxed">
          "This blog has become my go-to resource for staying ahead in engineering. The quality and depth of every article is incredible."
        </blockquote>
        <div className="mt-6">
          <p className="font-semibold text-foreground">Michael Torres</p>
          <p className="text-sm text-muted-foreground">Senior Engineer, ScaleCo</p>
        </div>
      </div>
    </section>

    {/* Newsletter CTA */}
    <section className="blue-section px-6 py-20 md:py-28 blogs-gradient-newsletter">
      <div className="relative z-10 mx-auto max-w-3xl text-center fade-in-up">
        <FaBook size={40} className="mx-auto mb-4 text-secondary" />
        <h2 className="font-display text-3xl font-bold text-primary-foreground md:text-5xl">
          Never Miss a Post
        </h2>
        <p className="mt-4 text-primary-foreground/70 text-lg">
          Get the latest insights delivered straight to your inbox. No spam, just quality content.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            placeholder="Enter your email"
            className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 h-12 rounded-xl flex-1 border px-4"
          />
          <button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold shadow-glow-yellow px-8 py-3 rounded-lg transition-all">
            Subscribe
          </button>
        </div>
        <p className="mt-3 text-xs text-primary-foreground/50">Join 5,000+ subscribers. Unsubscribe anytime.</p>
      </div>
    </section>
  </div>
);
};

export default Blog;
